import React, { useCallback, useEffect, useRef } from 'react';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Box, Button, Container, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
    getUnhandledErrorMessage,
    shouldIgnoreWindowError,
    subscribeGlobalError,
} from '../utils/errorHandling';

const ErrorFallback = ({ onRetry }) => (
    <Box
        sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f7f7f7',
            px: 2,
        }}
    >
        <Container maxWidth="sm">
            <Box
                sx={{
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                    px: { xs: 3, sm: 5 },
                    py: { xs: 4, sm: 6 },
                }}
            >
                <ErrorOutlineIcon sx={{ fontSize: 56, color: '#b45309', mb: 1 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#242424', mb: 1 }}>
                    Something went wrong
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#64748b', mb: 3, lineHeight: 1.6 }}>
                    An unexpected error occurred while loading this page. Your progress may still be saved.
                    Please try again, or reload the page.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={onRetry}
                        sx={{
                            backgroundColor: '#236734',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { backgroundColor: '#1b5228' },
                        }}
                    >
                        Try again
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => window.location.reload()}
                        sx={{ textTransform: 'none', px: 3, color: '#242424', borderColor: '#242424' }}
                    >
                        Reload page
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 3 }}>
                    Need help? Call <strong>(08) 9362 2100</strong>
                </Typography>
            </Box>
        </Container>
    </Box>
);

class ErrorBoundary extends React.Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // eslint-disable-next-line no-console
        console.error('Uncaught application error', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <ErrorFallback onRetry={() => this.setState({ hasError: false })} />;
        }
        return this.props.children;
    }
}

const GlobalErrorListeners = ({ children }) => {
    const { enqueueSnackbar } = useSnackbar();
    const lastMessageRef = useRef({ text: '', at: 0 });

    const notify = useCallback((message, variant = 'error') => {
        if (!message) return;

        const now = Date.now();
        if (message === lastMessageRef.current.text && now - lastMessageRef.current.at < 2500) {
            return;
        }
        lastMessageRef.current = { text: message, at: now };
        enqueueSnackbar(message, { variant, autoHideDuration: 6000, preventDuplicate: true });
    }, [enqueueSnackbar]);

    useEffect(() => {
        const unsubscribe = subscribeGlobalError((payload) => {
            const message = typeof payload === 'string' ? payload : payload?.message;
            notify(message, payload?.variant || 'error');
        });

        const onUnhandledRejection = (event) => notify(getUnhandledErrorMessage(event.reason));

        const onWindowError = (event) => {
            if (!event.error || shouldIgnoreWindowError(event.message)) return;
            notify('Something went wrong. Please try again.');
        };

        window.addEventListener('unhandledrejection', onUnhandledRejection);
        window.addEventListener('error', onWindowError);

        return () => {
            unsubscribe();
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
            window.removeEventListener('error', onWindowError);
        };
    }, [notify]);

    return <ErrorBoundary>{children}</ErrorBoundary>;
};

const GlobalErrorHandler = ({ children }) => (
    <SnackbarProvider
        maxSnack={3}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: '70px' }}
    >
        <GlobalErrorListeners>{children}</GlobalErrorListeners>
    </SnackbarProvider>
);

export default GlobalErrorHandler;
